import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Link2, Copy, Check, ExternalLink, Eye, X, Clock, AlertTriangle } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useAuth } from "@/lib/auth";
import { encryptUrl, encryptDouble, decryptInnerPayload, decryptUrl } from "@/lib/safelink-crypto";
import { logSafelink } from "@/lib/safelink-log";

export const Route = createFileRoute("/admin/safelink")({
  head: () => ({
    meta: [
      { title: "مولّد الرابط المحمي — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SafelinkGenerator,
});

type ContentKind = "movie" | "series" | "trailer";

interface PreviewData {
  url: string;
  kind?: string;
  slug?: string;
  title?: string;
  exp?: number;
  iat?: number;
  expired: boolean;
}

function maskUrl(u: string): string {
  try {
    const url = new URL(u);
    const path = url.pathname;
    const shortPath = path.length > 24 ? path.slice(0, 12) + "…" + path.slice(-8) : path;
    return `${url.protocol}//${url.hostname}${shortPath}`;
  } catch {
    return u.length > 40 ? u.slice(0, 24) + "…" + u.slice(-12) : u;
  }
}

function SafelinkGenerator() {
  const { isAuthed, email } = useAuth();
  const navigate = useNavigate();
  const [target, setTarget] = useState("");
  const [copied, setCopied] = useState(false);
  const [generated, setGenerated] = useState("");
  const [working, setWorking] = useState(false);
  const [mode, setMode] = useState<"safelink" | "redirect" | "player">("safelink");
  const [kind, setKind] = useState<ContentKind>("movie");
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [ttlHours, setTtlHours] = useState<number>(6);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthed) navigate({ to: "/login" });
  }, [isAuthed, navigate]);

  useEffect(() => {
    let cancelled = false;
    const value = target.trim();
    if (!value || typeof window === "undefined") { setGenerated(""); return; }
    setWorking(true);
    const enc = mode === "player"
      ? encryptDouble(value, { kind, slug: slug || undefined, title: title || undefined, ttlSec: ttlHours * 3600 })
      : encryptUrl(value);
    enc
      .then((token) => {
        if (cancelled) return;
        const path = mode === "redirect" ? "/redirect" : mode === "player" ? "/play" : "/safelink";
        const u = new URL(path, window.location.origin);
        u.searchParams.set("t", token);
        if (mode === "safelink") u.searchParams.set("d", "1");
        if (mode === "player") {
          u.searchParams.set("kind", kind);
          if (slug) u.searchParams.set("slug", slug);
        }
        setGenerated(u.toString());
      })
      .catch(() => { if (!cancelled) setGenerated(""); })
      .finally(() => { if (!cancelled) setWorking(false); });
    return () => { cancelled = true; };
  }, [target, mode, kind, slug, title, ttlHours]);

  async function copy() {
    if (!generated) return;
    await navigator.clipboard.writeText(generated);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    logSafelink({ source: "admin", status: "generated", kind, slug: slug || undefined, userId: email ?? null, note: "copied" });
  }

  async function openPreview() {
    setPreviewOpen(true);
    setPreviewData(null);
    setPreviewErr(null);
    try {
      const url = new URL(generated);
      const token = url.searchParams.get("t");
      if (!token) throw new Error("لا يوجد رمز في الرابط");
      if (mode === "player") {
        const inner = await decryptUrl(token);
        const p = await decryptInnerPayload(inner);
        setPreviewData(p);
      } else {
        const real = await decryptUrl(token);
        setPreviewData({ url: real, expired: false });
      }
    } catch {
      setPreviewErr("تعذّر فك تشفير الرابط للمعاينة.");
    }
  }

  if (!isAuthed) return null;

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-10 max-w-2xl">
        <BackToDashboard className="mb-4" />

        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="grid place-items-center w-12 h-12 rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
              <Link2 className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold">مولّد الرابط المحمي</h1>
              <p className="text-xs text-muted-foreground">حوّل أي رابط إلى رابط آمن قابل للانتهاء</p>
            </div>
          </div>

          <label className="block text-sm font-bold">الرابط الأصلي</label>
          <input
            type="url"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="https://example.com/file.mp4"
            className="w-full mt-1.5 px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <fieldset className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <legend className="text-sm font-bold mb-1">نوع الرابط المُولَّد</legend>
            {([
              { v: "safelink", label: "تحميل (Safelink)", desc: "صفحة فاصلة + فتح الرابط" },
              { v: "player",   label: "مشغّل RawanUI",    desc: "طبقتا تشفير + صلاحية" },
              { v: "redirect", label: "إعادة توجيه",       desc: "تحويل فوري لعنوان آخر" },
            ] as const).map((opt) => (
              <label
                key={opt.v}
                className={`flex flex-col gap-1 p-3 rounded-lg border cursor-pointer transition ${
                  mode === opt.v
                    ? "border-primary bg-primary/10"
                    : "border-border bg-background hover:bg-accent"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-bold">
                  <input
                    type="radio"
                    name="link-mode"
                    checked={mode === opt.v}
                    onChange={() => setMode(opt.v)}
                    className="accent-[color:var(--primary)]"
                  />
                  {opt.label}
                </span>
                <span className="text-[11px] text-muted-foreground">{opt.desc}</span>
              </label>
            ))}
          </fieldset>

          {mode === "player" && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg border border-border bg-background">
              <div>
                <label className="block text-xs font-bold mb-1">نوع المحتوى</label>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as ContentKind)}
                  className="w-full px-2 py-2 rounded bg-card border border-border text-sm"
                >
                  <option value="movie">فيلم</option>
                  <option value="series">مسلسل / حلقة</option>
                  <option value="trailer">ترايلر</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> الصلاحية (ساعات)
                </label>
                <input
                  type="number"
                  min={0}
                  max={168}
                  value={ttlHours}
                  onChange={(e) => setTtlHours(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full px-2 py-2 rounded bg-card border border-border text-sm"
                />
                <p className="text-[10px] text-muted-foreground mt-1">0 = بدون انتهاء</p>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">معرّف العنصر (اختياري)</label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="my-movie-slug"
                  className="w-full px-2 py-2 rounded bg-card border border-border text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">عنوان وصفي (اختياري)</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="عنوان للعرض في المعاينة"
                  className="w-full px-2 py-2 rounded bg-card border border-border text-sm"
                />
              </div>
            </div>
          )}

          {working && (
            <p className="mt-3 text-xs text-muted-foreground">جارٍ تشفير الرابط…</p>
          )}
          {generated && (
            <div className="mt-5">
              <label className="block text-sm font-bold">الرابط المحمي (مشفر AES-GCM 256)</label>
              <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-2">
                <input
                  readOnly
                  value={generated}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-xs sm:text-sm font-mono text-muted-foreground focus:outline-none"
                />
                <button onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-card border border-border hover:bg-accent text-sm shrink-0">
                  {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                  <span className="hidden sm:inline">{copied ? "تم النسخ" : "نسخ"}</span>
                </button>
                <button onClick={openPreview} className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-card border border-primary/40 hover:bg-primary/10 text-sm shrink-0">
                  <Eye className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline">معاينة</span>
                </button>
                <a href={generated} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-3 text-sm shrink-0">
                  <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">فتح</span>
                </a>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                شارك هذا الرابط بدل الرابط الأصلي. الزر <strong>معاينة</strong> يكشف لك آمناً ما الذي سيشغّله قبل فتحه فعلياً.
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* مودال المعاينة */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-extrabold flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" /> معاينة الرابط
              </h2>
              <button onClick={() => setPreviewOpen(false)} aria-label="إغلاق">
                <X className="h-5 w-5" />
              </button>
            </div>

            {!previewData && !previewErr && (
              <p className="text-sm text-muted-foreground">جارٍ فك التشفير…</p>
            )}

            {previewErr && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {previewErr}
              </div>
            )}

            {previewData && (
              <div className="space-y-3 text-sm">
                {previewData.expired && (
                  <div className="flex items-center gap-2 p-2 rounded bg-destructive/15 border border-destructive/30 text-destructive font-bold">
                    <AlertTriangle className="h-4 w-4" /> هذا الرابط منتهي الصلاحية.
                  </div>
                )}
                <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2">
                  {previewData.title && (<>
                    <span className="text-muted-foreground">العنوان:</span>
                    <span className="font-bold">{previewData.title}</span>
                  </>)}
                  {previewData.kind && (<>
                    <span className="text-muted-foreground">النوع:</span>
                    <span className="font-bold">{previewData.kind}</span>
                  </>)}
                  {previewData.slug && (<>
                    <span className="text-muted-foreground">المعرّف:</span>
                    <span className="font-mono text-xs">{previewData.slug}</span>
                  </>)}
                  {previewData.iat && (<>
                    <span className="text-muted-foreground">تاريخ الإصدار:</span>
                    <span>{new Date(previewData.iat).toLocaleString("ar")}</span>
                  </>)}
                  {previewData.exp && (<>
                    <span className="text-muted-foreground">ينتهي في:</span>
                    <span className={previewData.expired ? "text-destructive font-bold" : "font-bold"}>
                      {new Date(previewData.exp).toLocaleString("ar")}
                    </span>
                  </>)}
                  <span className="text-muted-foreground">الوجهة (مخفّاة):</span>
                  <span className="font-mono text-xs break-all">{maskUrl(previewData.url)}</span>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-primary">إظهار الوجهة الكاملة (للأدمن فقط)</summary>
                  <div className="mt-2 p-2 rounded bg-background border border-border font-mono text-[11px] break-all">
                    {previewData.url}
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
